import { useAtomValue } from "jotai";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Page } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import { Button, Color, Flex, Icon, Link, Scroller, Spinner, Text } from "src/elements";
import { MerchflowHeader } from "../../components/merchflows/MerchflowHeader/MerchflowHeader";
import { basePogTemplateAtoms } from "./store/atom";

import { PogOverview } from "../Merchflows_merchflowId_Stores_storeCode/components/PogOverview";
import { pages } from "src/utils";
import { useApi } from "src/api";
import { RoutePutBasePogSelectedStatus } from "@CommonApi/pogger/base-pog/status";
import { useBasePlanogramReviewModal } from "src/modals/BasePlanogramReview/store/hooks";
import { BasePogWithId } from "./store/types";
import { routeToUrl } from "@CommonUtils/routes";
import { RoutePostMerchflows_merchflowId_ExportsBatch } from "@CommonApi/merchflows/_merchflowId_/exports/batch";

export const PageMerchflowTemplateBasePogs = () => {
  const { merchflowId: _merchflowId, templateId: _templateId } = useParams<{
    merchflowId: string;
    templateId: string;
  }>();
  const merchflowId = Number(_merchflowId);
  const templateId = String(_templateId);
  const curRef = useRef({ merchflowId: 0, templateId: "" });

  const { postExportBatchPogsApi, putBasePogStatusApi } = useApi();
  const { hasLoader } = useLoaders();
  const basePogs = useAtomValue(basePogTemplateAtoms);
  const [selection, setSelection] = useState<number[]>([]);

  const isBasePogListLoading = hasLoader("loadAllBasePogOfTemplate");
  const isFavoriteLoading = hasLoader(RoutePutBasePogSelectedStatus);
  const isExportLoading = hasLoader(RoutePostMerchflows_merchflowId_ExportsBatch);
  const { openBasePlanogramReviewModal } = useBasePlanogramReviewModal();

  const showWarningBasePogs = basePogs.reduce<Record<string, boolean>>((result, basePog) => {
    if (basePog.is_favourite) {
      result[String(basePog.id)] = true;
    }
    return result;
  }, {});

  const onClickBasePog = (basePogWithId: BasePogWithId) => {
    openBasePlanogramReviewModal({
      merchflowId,
      basePogId: basePogWithId.id,
      basePog: basePogWithId.pog_data,
    });
  };

  const onClickFavorite = async (selection: number[]) => {
    const response = await putBasePogStatusApi({
      is_favourite: selection,
    });

    if (response) {
      // loadAllBasePogOfTemplate(templateId, merchflowId);
      setSelection([]);
    }
  };

  const onClickUnFavorite = async (selection: number[]) => {
    const response = await putBasePogStatusApi({
      is_not_favourite: selection,
    });

    if (response) {
      // loadAllBasePogOfTemplate(templateId, merchflowId);
      setSelection([]);
    }
  };

  const exportSelectedStorePogs = async () => {
    const response = await postExportBatchPogsApi(merchflowId, {
      type: "psaV2",
      base_pog_ids: selection,
    });

    if (response?.status === "DONE") {
      setSelection([]);
    }
  };

  useEffect(() => {
    const { merchflowId: curMerchflowId, templateId: curTemplateId } = curRef.current;

    if (merchflowId !== curMerchflowId || templateId !== curTemplateId) {
      // loadAllBasePogOfTemplate(templateId, merchflowId);
      curRef.current = {
        merchflowId,
        templateId,
      };
    }
  }, [templateId, merchflowId]);

  return (
    <Page>
      <MerchflowHeader merchflowId={merchflowId} />

      <Flex minHeight="0" flexGrow column gap="5px">
        <Flex justify="space-between" align="center">
          <Link to={routeToUrl(pages.merchflowTemplates, { merchflowId })}>
            <Flex height="24px" align="center" gap="5px">
              <Icon name="arrowLeft" />

              <Text variant="small1" color={Color.textMain}>
                Back to Review Screen
              </Text>
            </Flex>
          </Link>
          <Flex gap="10px">
            <Button
              isLoading={isFavoriteLoading}
              dropdown={[
                {
                  label: "Favorite selected",
                  onClick: () => onClickFavorite(selection),
                  isDisabled: selection.length === 0,
                },
                {
                  label: "Un-favorite selected",
                  onClick: () => onClickUnFavorite(selection),
                  isDisabled: selection.length === 0,
                },
              ]}
            >
              Favorite
            </Button>

            <Button
              isLoading={isExportLoading}
              dropdown={[
                {
                  label: "Export selected",
                  onClick: exportSelectedStorePogs,
                  isDisabled: selection.length === 0,
                },
              ]}
            >
              Exports
            </Button>
          </Flex>
        </Flex>

        <Flex minHeight="0" flexGrow borderColor={Color.lightGrayHover} borderRadius="3px">
          {!isBasePogListLoading ? (
            <Scroller>
              <Flex padding="20px" gap="20px" flexWrap="wrap">
                {basePogs.map((basePog, i) => (
                  <Flex
                    key={i}
                    maxWidth="50%"
                    position="relative"
                    onClick={() => onClickBasePog(basePog)}
                  >
                    <PogOverview
                      pog={basePog.pog_data}
                      label={`Basepog: ${basePog.id}`}
                      selectionId={basePog.id}
                      selection={selection}
                      setSelection={setSelection}
                      status={
                        Object.values(showWarningBasePogs).length > 1 &&
                        showWarningBasePogs[String(basePog.id)]
                          ? "ERROR"
                          : undefined
                      }
                    />
                    <Flex position="absolute" top="-10px" left="-10px">
                      <Icon name={basePog.is_favourite ? "starFilled" : "starUnfilled"} />
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            </Scroller>
          ) : (
            <Flex width="100%" align="center" justify="center">
              <Spinner size="big" />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Page>
  );
};
